import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake, PartyPopper } from "lucide-react";
import TierDot from "@/components/TierDot";
import { tierFor } from "@/lib/tiers";

/**
 * "Thank / like" toggle button. One like per person per (contextType, contextId).
 * A thumbs-up icon (filled when active). When `showTier` is set (rows without an
 * avatar) a tiny tier dot is shown before the button. When a like pushes the
 * recipient across a tier boundary, a celebratory milestone toast is shown.
 */
export default function ThankButton({
    toUser,
    toName,
    contextType,
    contextId,
    initialCount = 0,
    showTier = false,
    size = "sm",
    onCount,
}) {
    const { user } = useAuth();
    const [thanked, setThanked] = useState(false);
    const [count, setCount] = useState(Number(initialCount) || 0);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!toUser || !contextId) return;
        let active = true;
        api.get("/thanks/status", { params: { to_user: toUser, context_type: contextType, context_id: contextId } })
            .then(({ data }) => { if (active) { setThanked(!!data.thanked); setCount(data.count); } })
            .catch(() => {});
        return () => { active = false; };
    }, [toUser, contextType, contextId]);

    const isSelf = !!(user && user.id === toUser);

    const toggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isSelf) return;
        if (!user) { toast.error("Log in to thank"); return; }
        if (busy) return;
        setBusy(true);
        try {
            const prev = count;
            const { data } = await api.post("/thanks", { to_user: toUser, context_type: contextType, context_id: contextId });
            setThanked(data.thanked);
            setCount(data.count);
            if (onCount) onCount(data.count);
            const before = tierFor(prev), after = tierFor(data.count);
            if (data.thanked && data.count > prev && before.name !== after.name) {
                const who = toName ? `${toName}` : "This member";
                toast.success(`${who} just reached ${after.name} tier!`, {
                    icon: <PartyPopper className="h-4 w-4" style={{ color: after.color }} />,
                    description: "Their ring color just leveled up.",
                });
            } else {
                toast.success(data.thanked ? "Thanked" : "Thank removed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send");
        } finally {
            setBusy(false);
        }
    };

    const dim = size === "md" ? "h-8 w-8" : "h-6 w-6";
    const icon = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
    return (
        <span className="inline-flex items-center gap-1.5">
            {showTier && <TierDot count={count} />}
            <button
                type="button"
                data-testid="thank-button"
                onClick={toggle}
                disabled={busy || isSelf}
                title={isSelf ? "This is you — others can thank you here" : (thanked ? "You thanked this — click to undo" : "Thank")}
                aria-label={isSelf ? "Your thanks" : "Thank"}
                className={`${dim} inline-flex items-center justify-center rounded-full border transition-colors ${isSelf ? "bg-[#141414] border-[#2a2a2a] cursor-default opacity-80 text-[#a0a0a0]" : thanked ? "bg-[#22c55e]/20 border-[#22c55e] text-[#22c55e]" : "bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500] hover:text-[#ff4500]"}`}
            >
                <Handshake className={icon} />
            </button>
        </span>
    );
}
